class RawDawgSellingPlanSelector extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select[name="selling_plan"]');
    this.controls = this.select ? [this.select] : Array.from(this.querySelectorAll('input[name="selling_plan"]'));
    this.form = document.getElementById(`product-form-${this.dataset.sectionId}`);
    this.variantInput = this.form?.querySelector('input[name="id"]');
    this.price = document.getElementById(`price-${this.dataset.sectionId}`);
    this.defaultSellingPlanName = this.dataset.defaultSellingPlanName || "";
    this.variants = this.readVariants();

    if (!this.controls.length || !this.variantInput || !this.price || !this.variants.length) return;

    this.onVariantChange = () => this.sync(Number(this.variantInput.value));
    this.onPlanChange = () => this.renderPrice();
    this.controls.forEach((control) => control.addEventListener("change", this.onPlanChange));
    this.variantInput.addEventListener("change", this.onVariantChange);

    this.priceObserver = new MutationObserver(() => {
      if (this.isRenderingPrice) return;
      this.renderPrice();
    });
    this.priceObserver.observe(this.price, { childList: true, subtree: true });
    this.sync(Number(this.variantInput.value || this.dataset.initialVariantId));
  }

  disconnectedCallback() {
    this.controls?.forEach((control) => control.removeEventListener("change", this.onPlanChange));
    this.variantInput?.removeEventListener("change", this.onVariantChange);
    this.priceObserver?.disconnect();
  }

  readVariants() {
    try {
      return JSON.parse(this.querySelector("[data-selling-plan-variants]")?.textContent || "{}").variants || [];
    } catch (error) {
      console.warn("Raw Dawg selling plan data could not be read.", error);
      return [];
    }
  }

  getSelectedValue() {
    if (this.select) return this.select.value;
    return this.controls.find((control) => control.checked)?.value || "";
  }

  getPlanForValue(variant, value) {
    return variant?.plans.find((plan) => String(plan.id) === String(value));
  }

  updateAvailability() {
    const availablePlanIds = new Set(this.variant.plans.map((plan) => String(plan.id)));
    const isAvailable = (value) => (value === "" ? !this.variant.requiresSellingPlan : availablePlanIds.has(String(value)));

    if (this.select) {
      Array.from(this.select.options).forEach((option) => {
        const enabled = isAvailable(option.value);
        option.disabled = !enabled;
        option.hidden = !enabled;
      });
      return;
    }

    this.controls.forEach((control) => {
      const enabled = isAvailable(control.value);
      control.disabled = !enabled;
      const option = control.closest(".product-selling-plans__option");
      if (option) option.hidden = !enabled;
    });
  }

  isValueAvailable(value) {
    if (this.select) {
      const option = Array.from(this.select.options).find((item) => String(item.value) === String(value));
      return Boolean(option && !option.disabled);
    }

    const control = this.controls.find((item) => String(item.value) === String(value));
    return Boolean(control && !control.disabled);
  }

  selectValue(value) {
    if (this.select) {
      const option = Array.from(this.select.options).find(
        (item) => String(item.value) === String(value) && !item.disabled
      );
      if (!option) return false;
      this.select.value = option.value;
      return true;
    }

    const selectedControl = this.controls.find(
      (control) => String(control.value) === String(value) && !control.disabled
    );
    if (!selectedControl) return false;
    this.controls.forEach((control) => {
      control.checked = control === selectedControl;
    });
    return true;
  }

  fallbackValue() {
    if (!this.variant.requiresSellingPlan && this.isValueAvailable("")) return "";
    const firstAvailablePlan = this.variant.plans.find((plan) => this.isValueAvailable(String(plan.id)));
    return firstAvailablePlan ? String(firstAvailablePlan.id) : "";
  }

  initialValue() {
    const exactDefaultPlan = this.variant.plans.find((plan) => plan.name === this.defaultSellingPlanName);
    return exactDefaultPlan ? String(exactDefaultPlan.id) : this.fallbackValue();
  }

  sync(variantId) {
    const previousValue = this.getSelectedValue();
    const previousPlanName = this.getPlanForValue(this.variant, previousValue)?.name || "";
    this.variant = this.variants.find((variant) => Number(variant.id) === Number(variantId));
    if (!this.variant) return;

    this.updateAvailability();

    let nextValue = previousValue;
    if (!this.hasSynced) {
      nextValue = this.initialValue();
    } else if (!this.isValueAvailable(nextValue)) {
      const matchingPlan = previousPlanName
        ? this.variant.plans.find((plan) => plan.name === previousPlanName)
        : null;
      nextValue = matchingPlan ? String(matchingPlan.id) : this.fallbackValue();
    }

    this.selectValue(nextValue);
    this.hasSynced = true;
    this.renderPrice();
  }

  renderPrice() {
    if (!this.variant) return;
    const selectedPlan = this.getPlanForValue(this.variant, this.getSelectedValue());
    const display = selectedPlan || this.variant;
    const compareAt = Number(display.compareAtPrice || 0);
    const currentPrice = Number(display.price || 0);
    const regularPrice = this.price.querySelector(".price__regular .price-item--regular");
    const salePrice = this.price.querySelector(".price__sale .price-item--sale");
    const comparePrice = this.price.querySelector(".price__sale .price-item--regular");
    const priceRoot = this.price.querySelector(".price");

    this.isRenderingPrice = true;
    if (regularPrice) regularPrice.textContent = display.priceFormatted;
    if (salePrice) salePrice.textContent = display.priceFormatted;
    if (comparePrice) comparePrice.textContent = display.compareAtPriceFormatted || this.variant.priceFormatted;
    if (priceRoot) priceRoot.classList.toggle("price--on-sale", compareAt > currentPrice);
    queueMicrotask(() => {
      this.isRenderingPrice = false;
    });
  }
}

if (!customElements.get("selling-plan-selector")) {
  customElements.define("selling-plan-selector", RawDawgSellingPlanSelector);
}
